import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, User, Sparkles } from 'lucide-react';
import { useLocale } from '../contexts/LocaleContext';
import { getAppLocale } from '../content/appLocale';
import LocaleSelector from '../components/LocaleSelector';

const Register = () => {
  const { locale } = useLocale();
  const copy = getAppLocale(locale);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');

  const [error, setError] = useState('');
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [acceptedLegal, setAcceptedLegal] = useState(false);

  const {
    register,
    loginWithGoogle,
    isFirebaseClientConfigured,
  } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!acceptedLegal) {
      setError(copy.auth.registerMustAcceptLegal);
      return;
    }
    setLoadingEmail(true);
    const result = await register(email, password, name);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setLoadingEmail(false);
  };

  const handleGoogle = async () => {
    setError('');
    if (!acceptedLegal) {
      setError(copy.auth.registerMustAcceptLegal);
      return;
    }
    setLoadingGoogle(true);
    const result = await loginWithGoogle();
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setLoadingGoogle(false);
  };

  return (
    <div className="min-h-screen bg-[#0B0B0D] flex">
      <div className="flex-1 flex items-center justify-center p-8 overflow-y-auto">
        <div className="max-w-md w-full py-8">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-gradient-to-br from-[#7C5CFF] to-[#A88BFF] p-3 rounded-2xl">
                <Sparkles className="w-8 h-8 text-white" strokeWidth={1.5} />
              </div>
            </div>
            <h1 className="text-4xl font-light tracking-tight text-[#F5F4FF] mb-2">{copy.auth.createAccount}</h1>
            <p className="text-[#B6B3C9]">{copy.auth.registerSubtitle}</p>
          </div>

          <div className="bg-[#141417] border border-[#2B2B35]/50 rounded-2xl p-8 shadow-[0_4px_24px_rgba(0,0,0,0.02)] space-y-8">
            {error && (
              <div data-testid="register-error-message" className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
                {error}
              </div>
            )}

            {!isFirebaseClientConfigured && (
              <div className="bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded-xl text-sm leading-relaxed">
                {copy.auth.firebaseRegisterHelp}
              </div>
            )}

            {isFirebaseClientConfigured && (
              <>
                <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-[#B6B3C9]">
                  <input
                    type="checkbox"
                    data-testid="register-legal-accept"
                    checked={acceptedLegal}
                    onChange={(e) => setAcceptedLegal(e.target.checked)}
                    className="mt-1 h-4 w-4 shrink-0 rounded border-[#2B2B35] bg-[#0B0B0D] text-[#7C5CFF] focus:ring-[#7C5CFF]/30"
                  />
                  <span>
                    {copy.auth.registerLegalIntro}{' '}
                    <Link to="/terms" className="text-[#7C5CFF] hover:text-[#6B4DFF] underline">
                      {copy.auth.registerTermsLink}
                    </Link>{' '}
                    {copy.auth.registerLegalAnd}{' '}
                    <Link to="/privacy" className="text-[#7C5CFF] hover:text-[#6B4DFF] underline">
                      {copy.auth.registerPrivacyLink}
                    </Link>
                    {copy.auth.registerLegalEnd}
                  </span>
                </label>

                <div>
                  <button
                    type="button"
                    data-testid="register-google-button"
                    disabled={loadingGoogle || loadingEmail || !acceptedLegal}
                    onClick={handleGoogle}
                    className="w-full flex items-center justify-center gap-2 border border-[#2B2B35] rounded-full px-8 py-3 font-medium text-[#F5F4FF] hover:bg-[#0B0B0D] transition-colors disabled:opacity-50"
                  >
                    {loadingGoogle ? copy.auth.openGoogle : (
                      <>
                        <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden>
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        {copy.auth.continueGoogle}
                      </>
                    )}
                  </button>
                </div>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-[#2B2B35]" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase tracking-wide">
                    <span className="bg-[#141417] px-2 text-[#8F8AA8]">{copy.auth.orRegisterEmail}</span>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-[#F5F4FF] mb-2">{copy.auth.fullName}</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#8F8AA8] w-5 h-5" strokeWidth={1.5} />
                      <input
                        data-testid="register-name-input"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 bg-[#0B0B0D] border border-[#2B2B35] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#7C5CFF]/20 focus:border-[#7C5CFF] transition-all text-[#F5F4FF] placeholder-[#8F8AA8]"
                        placeholder="Enter your full name"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#F5F4FF] mb-2">{copy.auth.emailAddress}</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#8F8AA8] w-5 h-5" strokeWidth={1.5} />
                      <input
                        data-testid="register-email-input"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 bg-[#0B0B0D] border border-[#2B2B35] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#7C5CFF]/20 focus:border-[#7C5CFF] transition-all text-[#F5F4FF] placeholder-[#8F8AA8]"
                        placeholder="you@example.com"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#F5F4FF] mb-2">{copy.auth.password}</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#8F8AA8] w-5 h-5" strokeWidth={1.5} />
                      <input
                        data-testid="register-password-input"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 bg-[#0B0B0D] border border-[#2B2B35] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#7C5CFF]/20 focus:border-[#7C5CFF] transition-all text-[#F5F4FF] placeholder-[#8F8AA8]"
                        placeholder="Create a password (stored in Firebase)"
                        required
                      />
                    </div>
                  </div>

                  <button
                    data-testid="register-submit-button"
                    type="submit"
                    disabled={loadingEmail || loadingGoogle || !acceptedLegal}
                    className="w-full bg-[#7C5CFF] text-white hover:bg-[#6B4DFF] rounded-full px-8 py-4 transition-all duration-300 shadow-[0_4px_14px_rgba(124,92,255,0.32)] hover:shadow-[0_6px_20px_rgba(124,92,255,0.45)] font-medium tracking-wide disabled:opacity-50"
                  >
                    {loadingEmail ? copy.auth.creatingAccount : copy.auth.createAccount}
                  </button>
                </form>
              </>
            )}

            <div className="text-center pt-2 space-y-3">
              {!isFirebaseClientConfigured && (
                <p className="text-xs text-[#8F8AA8]">
                  <Link to="/privacy" className="text-[#7C5CFF] hover:underline">
                    {copy.auth.registerPrivacyLink}
                  </Link>
                  <span className="mx-2 text-[#5c5870]">·</span>
                  <Link to="/terms" className="text-[#7C5CFF] hover:underline">
                    {copy.auth.registerTermsLink}
                  </Link>
                </p>
              )}
              <p className="text-sm text-[#B6B3C9]">
                {copy.auth.hasAccount}{' '}
                <Link to="/login" data-testid="login-link" className="text-[#7C5CFF] hover:text-[#6B4DFF] font-medium">
                  {copy.auth.signInLink}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 bg-cover bg-center relative" style={{
        backgroundImage: `url('${copy.media.authHero}')`
      }}>
        <div className="absolute right-6 top-6 z-20">
          <LocaleSelector />
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-[#050507]/75 to-[#050507]/40"></div>
        <div className="relative z-10 flex items-center justify-center p-12 text-white">
          <div className="max-w-md">
            <h2 className="text-4xl font-light mb-4">{copy.auth.premiumTitle}</h2>
            <p className="text-lg text-white/90">{copy.auth.premiumSubtitle}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
